/**
 * Fetching the Sovereign Pulse daily opportunity book.
 *
 * Both endpoints go through 146, which fetches upstream with the service key. That matters:
 * forwarding the reader's own token returns a stripped tier with no side, premium or section,
 * so the book would render with the mechanism fields blank and nothing to say why.
 */

import { elitePulseFetch } from '../utils/elitePulseFetch';
import type { DailyBook } from '../types/pulseDailyBook';

const DATES_ENDPOINT = '/api/ois/report-pulse-daily-book-dates';
const BOOK_ENDPOINT = '/api/ois/report-pulse-daily-book';

/** Session dates with an archived book, newest first. */
export async function fetchBookDates(signal?: AbortSignal): Promise<string[]> {
  const res = await elitePulseFetch(DATES_ENDPOINT, { signal });
  if (!res.ok) throw new Error(`dates request failed (${res.status})`);
  const json = await res.json();
  const dates = Array.isArray(json?.dates) ? json.dates : [];
  return dates.filter((d: unknown): d is string => typeof d === 'string' && !!d);
}

/**
 * The book for one session, or the live day when `date` is omitted.
 *
 * A 404 is a date with no book rather than a failure, and is returned as null so the caller
 * can say so plainly instead of showing an error for a day that simply has nothing.
 */
export async function fetchDailyBook(
  date?: string,
  signal?: AbortSignal,
): Promise<DailyBook | null> {
  const path = date ? `${BOOK_ENDPOINT}?date=${encodeURIComponent(date)}` : BOOK_ENDPOINT;
  const res = await elitePulseFetch(path, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`book request failed (${res.status})`);
  const json = await res.json();
  const data = json?.data;
  if (!data || typeof data !== 'object') return null;
  return {
    session_date: String(data.session_date || date || ''),
    market_state: String(data.market_state || ''),
    count: Number(data.count) || (Array.isArray(data.opportunities) ? data.opportunities.length : 0),
    opportunities: Array.isArray(data.opportunities) ? data.opportunities : [],
    // The session's underlying closes. Dropping it here left every archived tile with no
    // moneyness, so the Moneyness filter emptied any past session.
    spot_map: data.spot_map && typeof data.spot_map === 'object' ? data.spot_map : undefined,
  };
}

/** How often the live book is re-read. The generator updates it roughly every 20 minutes. */
export const LIVE_BOOK_POLL_MS = 20 * 60 * 1000;
